import React, { useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { useNavigate, useLocation, Outlet, useParams } from 'react-router-dom';
import { TabItem, TabMenu } from '@/UIlibrary/TabMenu/TabMenu';
import { Typography } from '@/UIlibrary/Typography/Typography';
import NaturalPersonRegistryTable from '@/pages/Onboarding/NaturalPerson/RegistryTable';
import LegalPersonRegistryTable from '@/pages/Onboarding/LegalPerson/RegistryTable';

export const TAB_NATURAL_PERSON = 'natural';
export const TAB_LEGAL_PERSON = 'legal';
export type TAB_KEYS = typeof TAB_NATURAL_PERSON | typeof TAB_LEGAL_PERSON;

const TAB_CONFIGS = {
	natural: {
		key: 'natural' as TAB_KEYS,
		translationKey: 'NaturalPerson',
		content: () => <NaturalPersonRegistryTable />
	},
	legal: {
		key: 'legal' as TAB_KEYS,
		translationKey: 'LegalPerson',
		content: () => <LegalPersonRegistryTable />
	}
};

const Clients: React.FC = () => {
	const t = useTranslations('Registries');
	const [tabs, setTabs] = useState<TabItem[]>([]);
	const [activeTab, setActiveTab] = useState(0);
	const navigate = useNavigate();
	const location = useLocation();
	const { clientId } = useParams();

	useEffect(() => {
		setTabs(getTabs());
		setActiveTabIndex(tabs);
	}, []);

	const setActiveTabIndex = (newTabs: TabItem[]) => {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.has('startingTab')) {
			const currentTab = urlParams.get('startingTab') as TAB_KEYS;
			const currentTabIndex = newTabs.findIndex(
				(tab) => tab.title === t(toTranslationKey(TAB_CONFIGS[currentTab].translationKey))
			);
			setActiveTab(currentTabIndex !== -1 ? currentTabIndex : 0);
		} else {
			setActiveTab(0);
		}
	};

	const getTabs = (): TabItem[] => {
		return Object.values(TAB_CONFIGS).map((tabConfig) => ({
			title: t(toTranslationKey(tabConfig.translationKey)),
			content: tabConfig.content(),
			badgeVisible: false
		}));
	};

	const getStartingTab = (index: number) => {
		switch (index) {
			case 0:
				return TAB_NATURAL_PERSON;
			case 1:
				return TAB_LEGAL_PERSON;
			default:
				return TAB_NATURAL_PERSON;
		}
	};

	const handleTabChange = (index: number) => {
		setActiveTab(index);
		const tabKey = Object.keys(TAB_CONFIGS).find(
			(key) =>
				t(toTranslationKey(TAB_CONFIGS[key as TAB_KEYS].translationKey)) === tabs[index].title
		) as TAB_KEYS;

		if (tabKey) {
			const params = new URLSearchParams();
			params.set('startingTab', getStartingTab(index));
			navigate(`${location.pathname}?${params.toString()}`);
		}
	};
	return (
		<main className="p-3 md:p-8">
			<div className="flex flex-col gap-6">
				{!clientId && (
					<>
						<Typography type="display">{t('ClientManagement')}</Typography>
						<Typography size="xl">{t('Title')}</Typography>
						<TabMenu
							activeTabIndex={activeTab}
							tabs={tabs}
							maxWidth={1500}
							onTabChanged={handleTabChange}
						/>
					</>
				)}
				<Outlet />
			</div>
		</main>
	);
};

export default Clients;
